import React, { useEffect, useRef } from 'react';
import { MediaTypePhotoPostEditorState } from '../../../modules/PostEditor/PostPreview/PostCardMediaPreviewInstagram/PostCardMediaPreviewInstagram.view';

export type CanvasImageViewProps = {
  media: Pick<MediaTypePhotoPostEditorState, 'url' | 'id' | '__typename'>;
  aspectRatio: number;
  objectFit: 'contain' | 'cover';
};

export const CanvasImageView = (props: CanvasImageViewProps): JSX.Element => {
  const { media, aspectRatio, objectFit } = props;
  const { url, id, __typename } = media;
  const key = `${id}-${__typename}`;
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // we use useEffect here only becayse we want to make sure it runs ater we mount the canvas to the stage.
  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (canvas && context) {
      const image = new Image();
      image.src = url;
      image.alt = `instagram - ${key}`;
      image.onload = () => {
        let newWidth: number;
        let newHeight: number;
        let xOffset = 0;
        let yOffset = 0;
        let imageWidth = image.width;
        let imageHeight = image.height;

        // Set canvas dimensions based on aspect ratio
        if (aspectRatio < 1) {
          newWidth = Math.ceil(image.height * aspectRatio);
          newHeight = image.height;
        } else if (aspectRatio > 1) {
          newHeight = Math.ceil(image.width / aspectRatio);
          newWidth = image.width;
        } else {
          newHeight = Math.min(imageHeight, imageWidth);
          newWidth = Math.min(imageHeight, imageWidth);
        }
        canvas.width = newWidth;
        canvas.height = newHeight;

        const canvasAspectRatio = canvas.width / canvas.height;
        const imageAspectRatio = image.width / image.height;

        switch (objectFit) {
          case 'contain':
            if (imageAspectRatio > canvasAspectRatio) {
              // Scale image based on canvas width
              imageWidth = canvas.width;
              imageHeight = canvas.width / imageAspectRatio;
            } else {
              // Scale image based on canvas height
              imageHeight = canvas.height;
              imageWidth = canvas.height * imageAspectRatio;
            }

            // Calculate offsets to center the image within the canvas
            xOffset = (canvas.width - imageWidth) / 2;
            yOffset = (canvas.height - imageHeight) / 2;
            break;

          case 'cover':
            if (canvasAspectRatio > imageAspectRatio) {
              // Scale image based on canvas width to cover
              imageWidth = canvas.width;
              imageHeight = canvas.width / imageAspectRatio;
            } else {
              // Scale image based on canvas height to cover
              imageHeight = canvas.height;
              imageWidth = canvas.height * imageAspectRatio;
            }

            // Calculate offsets to center the image within the canvas
            xOffset = (canvas.width - imageWidth) / 2;
            yOffset = (canvas.height - imageHeight) / 2;
            break;
        }

        context.clearRect(0, 0, canvas.width, canvas.height);
        context.drawImage(image, xOffset, yOffset, imageWidth, imageHeight);
      };
    }
  }, [url, aspectRatio, objectFit, key]);

  return <canvas ref={canvasRef} />;
};
